import { useEffect, useState } from "react";
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from "lucide-react";

/**
 * ServerPagination — enterprise pagination bar for server-side lists.
 * Persists page-size per storageKey in localStorage.
 * Controls: First · Prev · Next · Last · Page X of Y · Rows N · Go to page.
 * Page size options: 25 / 50 / 100 / 250 / 500 (default 50).
 */
export default function ServerPagination({
  page, pageSize, total, pages, onPageChange, onPageSizeChange,
  storageKey = "app.pageSize", testidPrefix = "srv-pg",
  sizeOptions = [25, 50, 100, 250, 500],
}) {
  const [gotoStr, setGotoStr] = useState("");

  useEffect(() => {
    const saved = parseInt(localStorage.getItem(storageKey) || "0", 10);
    if (saved && saved !== pageSize && sizeOptions.includes(saved)) {
      onPageSizeChange?.(saved);
    }
    // eslint-disable-next-line
  }, []);

  const showFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showTo = Math.min(total, page * pageSize);
  const setSize = (v) => {
    localStorage.setItem(storageKey, String(v));
    onPageSizeChange?.(v);
  };
  const submitGoto = () => {
    const n = parseInt(gotoStr, 10);
    if (n >= 1 && n <= pages) { onPageChange?.(n); setGotoStr(""); }
  };
  const btn = "border border-input rounded-md h-8 w-8 flex items-center justify-center disabled:opacity-40 hover:border-primary hover:text-primary transition-colors";

  return (
    <div className="flex items-center justify-between flex-wrap gap-3 text-xs text-muted-foreground pt-2" data-testid={`${testidPrefix}-bar`}>
      <div data-testid={`${testidPrefix}-info`}>
        Showing <b className="text-foreground mono">{showFrom}</b>–<b className="text-foreground mono">{showTo}</b>{" "}
        of <b className="text-foreground mono">{total.toLocaleString()}</b>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="flex items-center gap-1">
          Rows
          <select
            data-testid={`${testidPrefix}-size`}
            value={pageSize}
            onChange={(e) => setSize(parseInt(e.target.value, 10))}
            className="h-8 rounded-md border border-input bg-background px-2"
          >
            {sizeOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <button data-testid={`${testidPrefix}-first`} disabled={page <= 1}
                onClick={() => onPageChange?.(1)} className={btn} title="First"><ChevronsLeft size={14} /></button>
        <button data-testid={`${testidPrefix}-prev`} disabled={page <= 1}
                onClick={() => onPageChange?.(Math.max(1, page - 1))} className={btn} title="Previous"><ChevronLeft size={14} /></button>
        <span className="mono px-1" data-testid={`${testidPrefix}-page`}>
          Page <b className="text-foreground">{page}</b> of <b className="text-foreground">{pages || 1}</b>
        </span>
        <button data-testid={`${testidPrefix}-next`} disabled={page >= pages}
                onClick={() => onPageChange?.(Math.min(pages, page + 1))} className={btn} title="Next"><ChevronRight size={14} /></button>
        <button data-testid={`${testidPrefix}-last`} disabled={page >= pages}
                onClick={() => onPageChange?.(pages)} className={btn} title="Last"><ChevronsRight size={14} /></button>
        <label className="flex items-center gap-1 ml-1">
          Go to
          <input
            data-testid={`${testidPrefix}-goto`}
            type="number" min={1} max={pages} value={gotoStr}
            onChange={(e) => setGotoStr(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") submitGoto(); }}
            onBlur={submitGoto}
            className="h-8 w-16 rounded-md border border-input bg-background px-2 text-center mono"
          />
        </label>
      </div>
    </div>
  );
}
